import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { apiJson } from '@/lib/api';
import type { AdminUserDetailResponse } from '@/lib/types';

function AdminUserHouseholdsPage() {
  const { userId } = Route.useParams();
  const id = Number(userId);
  const { getToken } = useAuth();

  const userQuery = useQuery({
    queryKey: ['admin', 'user-detail', id],
    queryFn: () => apiJson<AdminUserDetailResponse>(getToken, `/v1/admin/users/${id}`),
  });

  const d = userQuery.data;

  if (userQuery.isLoading || !d) {
    return (
      <div className='flex justify-center py-12'>
        <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
      </div>
    );
  }

  if (!d.households.length) {
    return <p className='text-sm text-gray-500'>This user has no households.</p>;
  }

  return (
    <div className='space-y-8'>
      {d.households.map((h) => (
        <div key={h.household_id} className='bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'>
          <div className='px-4 py-3 bg-gray-50 border-b border-gray-200 flex flex-wrap justify-between items-center gap-2'>
            <div>
              <h2 className='font-semibold text-gray-900'>Household #{h.household_id}</h2>
              <p className='text-xs text-gray-500'>
                Created {new Date(h.created_at).toLocaleDateString()} · {h.message_count} messages ·{' '}
                {h.expense_count} expenses
              </p>
            </div>
            <div className='flex gap-2'>
              <Link
                to='/admin/users/$userId/chat'
                params={{ userId }}
                search={{ hid: String(h.household_id) }}
                className='text-xs font-semibold text-primary hover:underline'
              >
                Chat
              </Link>
              <Link
                to='/admin/users/$userId/expenses'
                params={{ userId }}
                search={{ hid: String(h.household_id) }}
                className='text-xs font-semibold text-primary hover:underline'
              >
                Expenses
              </Link>
            </div>
          </div>
          <table className='w-full text-sm'>
            <thead className='bg-gray-50 text-left text-xs uppercase text-gray-500'>
              <tr>
                <th className='px-4 py-2'>User ID</th>
                <th className='px-4 py-2'>Name</th>
                <th className='px-4 py-2'>Email</th>
                <th className='px-4 py-2'>Phone</th>
                <th className='px-4 py-2'>Target</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-100'>
              {h.members.map((m) => (
                <tr key={m.user_id} className='hover:bg-gray-50'>
                  <td className='px-4 py-2 font-mono text-xs'>{m.user_id}</td>
                  <td className='px-4 py-2'>
                    {[m.first_name, m.last_name].filter(Boolean).join(' ') || '—'}
                  </td>
                  <td className='px-4 py-2'>{m.email ?? '—'}</td>
                  <td className='px-4 py-2'>{m.phone_number ?? '—'}</td>
                  <td className='px-4 py-2'>
                    {m.is_target ? (
                      <span className='text-xs font-semibold text-primary'>This user</span>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className='px-4 py-3 border-t border-gray-100 bg-gray-50/50'>
            <h3 className='text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2'>
              Beneficiaries
            </h3>
            {(h.beneficiaries?.length ?? 0) === 0 ? (
              <p className='text-sm text-gray-500'>None in this household.</p>
            ) : (
              <table className='w-full text-sm'>
                <thead className='text-left text-xs uppercase text-gray-500'>
                  <tr>
                    <th className='pr-4 py-1.5'>ID</th>
                    <th className='pr-4 py-1.5'>Name</th>
                    <th className='pr-4 py-1.5'>Type</th>
                    <th className='pr-4 py-1.5'>Birthdate</th>
                    <th className='py-1.5'>Created</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-100'>
                  {(h.beneficiaries ?? []).map((b) => (
                    <tr key={b.id} className='hover:bg-white'>
                      <td className='py-2 font-mono text-xs'>{b.id}</td>
                      <td className='py-2 font-medium text-gray-900'>{b.name}</td>
                      <td className='py-2'>{b.type}</td>
                      <td className='py-2 tabular-nums'>{b.birthdate ?? '—'}</td>
                      <td className='py-2 text-gray-500 text-xs'>
                        {new Date(b.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ))}

      <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'>
        <h2 className='text-sm font-semibold text-gray-900 mb-3'>Professional invites</h2>
        {d.professional_invites.length === 0 ? (
          <p className='text-sm text-gray-500'>None.</p>
        ) : (
          <table className='w-full text-sm'>
            <thead className='bg-gray-50 text-left text-xs uppercase text-gray-500'>
              <tr>
                <th className='px-4 py-2'>ID</th>
                <th className='px-4 py-2'>Household</th>
                <th className='px-4 py-2'>Co-parent user</th>
                <th className='px-4 py-2'>Professional email</th>
                <th className='px-4 py-2'>Pro user ID</th>
                <th className='px-4 py-2'>Status</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-100'>
              {d.professional_invites.map((inv) => (
                <tr key={inv.id} className='hover:bg-gray-50'>
                  <td className='px-4 py-2'>{inv.id}</td>
                  <td className='px-4 py-2'>{inv.household_id}</td>
                  <td className='px-4 py-2'>{inv.coparent_user_id}</td>
                  <td className='px-4 py-2'>{inv.professional_email}</td>
                  <td className='px-4 py-2'>{inv.professional_user_id ?? '—'}</td>
                  <td className='px-4 py-2'>
                    <span className='text-xs font-medium'>{inv.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute('/admin/users/$userId/households')({
  validateSearch: (search: Record<string, unknown>) => ({
    hid: typeof search.hid === 'string' ? search.hid : undefined,
  }),
  component: AdminUserHouseholdsPage,
});
